import {createRef, RefObject} from 'react';
import {MeshType} from 'api/generated';
import {RootState} from '@react-three/fiber';
import {Group, Mesh, PerspectiveCamera} from 'three';
import {ARModel} from 'components/ar/core';
import {create} from 'zustand';

namespace useARStore {
  export type ModelType = {
    id: string;
    data: MeshType;
    transform?: ARModel.TransformType;
  };
  export type ModelActiveType = {
    id: string;
    ref: RefObject<Group>;
    justPlaced: boolean;
  };
  export type GLType = RootState['gl'];
  export type SceneType = RootState['scene'];
  export type CameraType = RootState['camera'];
  export type FieldOfViewType = {
    aspectRatio: number;
    fov: number;
    radX: number;
    radY: number;
    maxX: number;
    maxY: number;
  };
}

const initialState = {
  meshes: [] as MeshType[],
  models: [] as useARStore.ModelType[],
  modelActive: {
    id: '',
    ref: createRef(),
    justPlaced: false,
  } as useARStore.ModelActiveType,
  modelData: null as null | Omit<useARStore.ModelType, 'id'>,
  screenshotData: '' as string,
  fieldOfView: null as null | useARStore.FieldOfViewType,
  hasHitTest: false as boolean,
  cameraRef: createRef() as RefObject<useARStore.CameraType>,
  compositeMeshRef: createRef() as RefObject<Mesh | null>,
  isModelInteractiveRef: createRef() as RefObject<boolean>,
};

type UseARStoreType = typeof initialState & {
  setMeshes: (meshes: MeshType[]) => void;
  setModels: (models: useARStore.ModelType[]) => void;
  setModelActive: (modelActive: Partial<Omit<useARStore.ModelActiveType, 'ref'>>) => void;
  setModelData: (modelData: null | Omit<useARStore.ModelType, 'id'>) => void;
  setScreenshotData: (screenshotData: string) => void;
  setFieldOfView: (camera: PerspectiveCamera) => void;
  setHasHitTest: (hasHitTest: boolean) => void;
  reset: () => void;
};

const useARStore = create<UseARStoreType>((set, get) => ({
  ...initialState,
  setMeshes: meshes => set({meshes}),
  setModels: models => set({models}),
  setModelActive: modelActive => {
    set({modelActive: {...get().modelActive, ...modelActive}});
  },
  setModelData: modelData => set({modelData}),
  setScreenshotData: screenshotData => set({screenshotData}),
  setFieldOfView: camera => {
    if (camera.fov !== get().fieldOfView?.fov) {
      const aspectRatio = window.innerWidth / window.innerHeight;
      const fov = camera.fov;
      const radY = (fov * Math.PI) / 180;
      const radX = 2 * Math.atan(Math.tan(radY / 2) * aspectRatio);
      const maxX = Math.tan(radX / 2);
      const maxY = Math.tan(radY / 2);
      set({fieldOfView: {aspectRatio, fov, radX, radY, maxX, maxY}});
    }
  },
  setHasHitTest: hasHitTest => set({hasHitTest}),
  reset: () => set(initialState),
}));

export default useARStore;
